import * as tslib_1 from "tslib";
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@services/logger/abstract.logger';
import { Setting } from 'main/app-state/current-user/settings';
import { NonPublicallyNullableStateManager } from 'main/app-state/generics/state-manager';
import * as moment from 'moment';
import * as numbro from 'numbro';
import { BehaviorSubject, of, } from 'rxjs';
import { map, pairwise, switchMap, tap, } from 'rxjs/operators';
var CSS_LANGUAGE_CLASS_PREFIX = 'lang-';
var EXTENDED_LOCALES = {
    'en': 'en-US',
    'ja': 'ja-JP',
    'de': 'de-DE',
    'fr': 'fr-FR',
    'ru': 'ru-RU',
    'zh': 'zh-CN',
    'es': 'es-ES',
    'ko': 'ko-KR',
    'tr': 'tr-TR',
    'pt': 'pt-PT',
};
[
    require('numbro/dist/languages/ja-JP.min'),
    require('numbro/dist/languages/de-DE.min'),
    require('numbro/dist/languages/fr-FR.min'),
    require('numbro/dist/languages/ru-RU.min'),
    require('numbro/dist/languages/zh-CN.min'),
    require('numbro/dist/languages/es-ES.min'),
    require('numbro/dist/languages/es-MX.min'),
    require('numbro/dist/languages/ko-KR.min'),
    require('numbro/dist/languages/tr-TR.min'),
    require('numbro/dist/languages/pt-PT.min'),
    require('numbro/dist/languages/pt-BR.min'),
].forEach(function (locale) { return numbro.registerLanguage(locale); });
/**
 * The current state is the language in which the app is displayed.
 * It's not its responsibility to send to server the user's currently set language (it isn't a
 * child of CurrentUserManager).
 */
var LanguageManager = /** @class */ (function (_super) {
    tslib_1.__extends(LanguageManager, _super);
    function LanguageManager(_injector, _appState, _dateAdapter) {
        var _this = _super.call(this) || this;
        _this._injector = _injector;
        _this._appState = _appState;
        _this._dateAdapter = _dateAdapter;
        _this._state = new BehaviorSubject(null);
        _this._document = _this._injector.get(DOCUMENT);
        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        _this._logStateChanges('Language', _this._injector.get(Logger));
        _this._state
            .pipe(pairwise())
            .subscribe(function (_a) {
            var prevLanguageCode = _a[0], languageCode = _a[1];
            if (!languageCode) {
                return;
            }
            moment.locale(languageCode);
            numbro.setLanguage(languageCode &&
                EXTENDED_LOCALES[languageCode] || EXTENDED_LOCALES['en']);
            _this._dateAdapter.setLocale(languageCode);
            if (prevLanguageCode != null) {
                _this._document.body.classList.remove("" + CSS_LANGUAGE_CLASS_PREFIX + prevLanguageCode);
            }
            _this._document.body.classList.add("" + CSS_LANGUAGE_CLASS_PREFIX + languageCode);
        });
        return _this;
    }
    /**
     * Should be called after either user's SettingsManager or AuthConfigManager are initialized.
     * Emits:
     * - true, when the initialization was successful
     * - false, in the unlikely case it initializes with a fallback value ('en'), because it
     *   was unable to determine the language from settings
     */
    LanguageManager.prototype.initialize = function () {
        var _this = this;
        // Because TranslateService needs an instance of AppStateFacade, it can't be called before AppStateFacade's constructor
        // completes its execution (otherwise, AppStateFacade would be undefined).
        // Furthermore, if TranslateService would be injected in the constructor, then it would be instantiated twice.
        return of(true).pipe(map(function () {
            var language;
            var hasInitializedSuccessfully = true;
            _this._translateService = _this._injector.get(TranslateService);
            _this._translateService.setDefaultLang('en');
            moment.tz.setDefault('UTC');
            if (_this._appState.user.value &&
                _this._appState.settings.value) {
                language = _this._appState.settings.get(Setting.currentCulture);
            }
            else if (_this._appState.authConfig.value) {
                language = _this._appState.authConfig.value.currentCulture;
            }
            else {
                language = 'en';
                hasInitializedSuccessfully = false;
            }
            return { language: language, hasInitializedSuccessfully: hasInitializedSuccessfully };
        }), switchMap(function (_a) {
            var language = _a.language, hasInitializedSuccessfully = _a.hasInitializedSuccessfully;
            return _this.set(language).pipe(map(function () { return hasInitializedSuccessfully; }));
        }));
    };
    LanguageManager.prototype.set = function (newLanguage) {
        var _this = this;
        return this._translateService
            .use(newLanguage)
            .pipe(tap(function () { return _this._state.next(newLanguage); }), map(function () { return true; }));
    };
    return LanguageManager;
}(NonPublicallyNullableStateManager));
export { LanguageManager };
